import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Shield, Mail, Lock, Globe, Server, Menu, X, Home, Sun, Moon } from 'lucide-react';
import { useTranslations } from '../i18n';
import { usePreferences } from '../contexts/PreferencesContext';
import { LanguagePickerModal } from '../components/ui/LanguagePickerModal';
import { SupportedLanguage } from '../lib/expenseTypeHelpers';
import { SEO } from '../components/ui/SEO';

export function TermsPage() {
    const { language: currentLang, t } = useTranslations();
    const { preferences: interfacePrefs, updatePreferences } = usePreferences();
    const [showMenu, setShowMenu] = useState(false);
    const [showLanguageModal, setShowLanguageModal] = useState(false);
    
    // Fallback if terms is not translated yet in other languages
    const rawTerms = typeof (t.landing as any).terms === 'object' ? (t.landing as any).terms : {};

    const terms = {
        title: rawTerms.title || "Terms and Conditions",
        lastUpdated: rawTerms.lastUpdated || "Last updated: March 25, 2026",
        introduction: rawTerms.introduction || "By accessing or using MyCarCosts, you agree to be bound by these Terms and Conditions. If you do not agree, please do not use our service.",
        acceptance: { 
            title: rawTerms.acceptance?.title || "1. Acceptance of Terms", 
            description: rawTerms.acceptance?.description || "These terms constitute a legally binding agreement between you and MyCarCosts regarding your use of our application and services." 
        },
        service: { 
            title: rawTerms.service?.title || "2. Description of Service", 
            description: rawTerms.service?.description || "MyCarCosts provides a platform for vehicle expense tracking, maintenance management, and financial analysis. This includes AI-powered receipt parsing and data visualization tools." 
        },
        responsibilities: { 
            title: rawTerms.responsibilities?.title || "3. User Responsibilities", 
            description: rawTerms.responsibilities?.description || "You are responsible for the accuracy of any data you input, including mileage, VIN numbers, and costs. You must keep your login credentials (email/magic link) secure and not share them with unauthorized parties." 
        },
        plans: { 
            title: rawTerms.plans?.title || "4. Service Plans and Limits", 
            description: rawTerms.plans?.description || "We offer Free and Pro plans with different storage, car count, and upload limits. We reserve the right to modify these limits or introduce new subscription tiers at any time." 
        },
        disclaimer: { 
            title: rawTerms.disclaimer?.title || "5. Disclaimers and Accuracy", 
            description: rawTerms.disclaimer?.description || "MyCarCosts is an information and organization tool. We do not provide official vehicle health certifications. AI-parsed data from receipts should always be verified by the user for accuracy." 
        },
        liability: { 
            title: rawTerms.liability?.title || "6. Limitation of Liability", 
            description: rawTerms.liability?.description || "MyCarCosts is provided 'as-is'. We are not liable for any mechanical issues, financial losses, or data inaccuracies resulting from the use of our application." 
        },
        termination: { 
            title: rawTerms.termination?.title || "7. Termination", 
            description: rawTerms.termination?.description || "You may stop using the service and delete your data at any time. We reserve the right to suspend or terminate accounts that violate these terms or engage in abusive behavior." 
        }
    };

    return (
        <div className="min-h-screen bg-transparent font-primary text-slate-900 dark:text-slate-100 flex flex-col">
            <SEO 
                title={`${terms.title} | MyCarCosts`}
                description="Terms and conditions for using the MyCarCosts application."
            />
            <header className="sticky top-0 z-50 w-full border-b border-slate-200 dark:border-slate-800 bg-[#EEF5FB]/80 dark:bg-[#000D19]/80 backdrop-blur-md">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => setShowMenu(true)}
                            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors text-blue-600 dark:text-blue-400"
                        >
                            <Menu size={20} />
                        </button>
                        <span className="text-xl font-bold tracking-tight">{terms.title}</span>
                    </div>
                </div>
            </header>

            {/* Side Menu Drawer */}
            {showMenu && (
                <div className="fixed inset-0 z-[100] flex items-start justify-start">
                    <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowMenu(false)} />
                    <div className="relative w-[280px] h-full bg-white dark:bg-slate-900 shadow-2xl animate-in slide-in-from-left duration-300 flex flex-col">
                        <div className="p-6 border-b border-gray-100 dark:border-white/5 flex items-center justify-between">
                            <h2 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-widest">{t.common.menu}</h2>
                            <button onClick={() => setShowMenu(false)} className="text-gray-400 hover:text-red-500 transition-colors"><X size={20} /></button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4 space-y-2 no-scrollbar">
                            <Link
                                to="/"
                                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-white/5 text-gray-700 dark:text-gray-300 transition-colors"
                            >
                                <div className="p-2 bg-blue-50 dark:bg-blue-900/40 text-blue-600 rounded-lg"><Home size={18} /></div>
                                <span className="text-sm font-bold">{t.common.home}</span>
                            </Link>
                            <button
                                onClick={() => { setShowLanguageModal(true); setShowMenu(false); }}
                                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-white/5 text-gray-700 dark:text-gray-300 transition-colors"
                            >
                                <div className="p-2 bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 rounded-lg"><Globe size={18} /></div>
                                <span className="text-sm font-bold">{t.settings?.language || 'Language'}</span>
                            </button>
                            <button
                                onClick={() => { updatePreferences({ theme: interfacePrefs?.theme === 'light' ? 'dark' : 'light' }); setShowMenu(false); }}
                                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-white/5 text-gray-700 dark:text-gray-300 transition-colors"
                            >
                                <div className="p-2 bg-amber-50 dark:bg-amber-900/40 text-amber-600 rounded-lg">
                                    {interfacePrefs?.theme === 'light' ? <Sun size={18} /> : <Moon size={18} />}
                                </div>
                                <span className="text-sm font-bold">{t.settings?.interfaceTheme || 'Design'}</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <LanguagePickerModal 
                isOpen={showLanguageModal}
                title={t.settings?.language || 'Interface Language'}
                currentLanguage={(interfacePrefs?.language as SupportedLanguage) || currentLang || 'en'}
                onClose={() => setShowLanguageModal(false)}
                onSelect={(lang: SupportedLanguage) => {
                    updatePreferences({ language: lang });
                    setShowLanguageModal(false);
                }}
            />

            <main className="flex-1 max-w-4xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
                <div className="mb-12">
                    <h1 className="text-4xl font-black mb-4">{terms.title}</h1>
                    <p className="text-slate-500 dark:text-slate-400 font-medium italic">
                        {terms.lastUpdated}
                    </p>
                </div>

                <div className="space-y-12 pb-32">
                    <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed font-medium">
                        {terms.introduction}
                    </p>

                    <div className="grid sm:grid-cols-2 gap-8">
                        {[
                            { icon: FileText, data: terms.acceptance, color: 'blue' },
                            { icon: Globe, data: terms.service, color: 'indigo' },
                            { icon: Shield, data: terms.responsibilities, color: 'green' },
                            { icon: Server, data: terms.plans, color: 'amber' },
                            { icon: Lock, data: terms.disclaimer, color: 'purple' },
                            { icon: Lock, data: terms.liability, color: 'red' },
                            { icon: X, data: terms.termination, color: 'slate' }
                        ].map((section, idx) => (
                            <section key={idx} className="space-y-4">
                                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center
                                    ${section.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : ''}
                                    ${section.color === 'indigo' ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400' : ''}
                                    ${section.color === 'green' ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' : ''}
                                    ${section.color === 'amber' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400' : ''}
                                    ${section.color === 'purple' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400' : ''}
                                    ${section.color === 'red' ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' : ''}
                                    ${section.color === 'slate' ? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400' : ''}
                                `}>
                                    <section.icon size={24} />
                                </div>
                                <h2 className="text-xl font-bold">{section.data.title}</h2>
                                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                                    {section.data.description}
                                </p>
                            </section>
                        ))}
                    </div>
                </div>

                <div className="mt-20 pt-12 border-t border-slate-200 dark:border-slate-800 text-center">
                    <p className="text-slate-500 mb-6">
                        {t.landing.footer.questions}
                    </p>
                    <a
                        href={`mailto:${t.landing.footer.contactEmail}`}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-2xl font-bold shadow-lg shadow-blue-500/20 active:scale-95 transition-all"
                    >
                        <Mail size={18} />
                        {t.landing.footer.contactEmail}
                    </a>
                </div>
            </main>
        </div>
    );
}
